// UnifiedEmail — one normalized shape for every email surface (email-surface unification).
// /inbox groups return InboxMessage (flat from, snippet); the account endpoint returns
// ClientEmail (nested from, bodyPreview, nested flag). The adapters below fold both into
// this shape so EmailRow/useEmailActions never care which endpoint a row came from.

import type { ClientEmail, GraphEmailAddress, InboxGroup, InboxMessage } from "@/lib/api";

export interface UnifiedEmail {
  id: string;
  /** Mailbox the message lives in — every action endpoint keys on (mailbox, messageId). */
  mailbox: string;
  /** Account the mailbox/action context belongs to. */
  accountId: string;
  subject: string | null;
  snippet: string | null;
  receivedDateTime: string;
  fromName: string | null;
  fromAddress: string | null;
  toRecipients?: GraphEmailAddress[];
  ccRecipients?: GraphEmailAddress[];
  isRead: boolean;
  hasAttachments: boolean;
  flagStatus?: string;
  categories: string[];
  // Claim/pin overlays
  taskStatus?: string;
  assignedToUserId?: string;
  assignedToName?: string;
  taskNotes?: string;
  pinned: boolean;
  pinnedProjectId?: string;
  pinnedProjectTitle?: string;
  conversationId?: string | null;
  // Account-surface extras
  via?: "client-mailbox" | "contact-match";
  isPersonalMailbox?: boolean;
  folder?: string;
  /** ENG-98: who a transport-rule BCC copy was originally sent to (the shared mailbox is
   * in neither To nor Cc); null/absent = genuinely addressed to this mailbox — no tag. */
  originallyTo?: { name: string; address: string } | null;
  /** v1.5.69: every internal staff To/Cc recipient except the mailbox itself — feeds the
   * multi-staff double-work badge ("→ Jennifer +1" / "Jennifer +1"). */
  internalRecipients?: { name: string; address: string }[];
  /** True on rows from GET /api/inbox/mine ("My Mail") — claim/pin/assign never apply. */
  isOwnPersonalMailbox?: boolean;
}

/**
 * InboxMessage -> ClientEmail, for merging "Load older" pages into the account page's
 * Shared Inbox tab (v1.5.66). GET /api/inbox/older returns the /inbox row shape while that
 * tab renders the /api/accounts/:id/emails shape, so the two must be reconciled somewhere;
 * doing it here keeps the four-hand-written-row-shapes problem that v1.5.14 ended from
 * coming back.
 *
 * The overlays (claim/pin/assign), categories, flag and conversation id already arrive on
 * the row — /api/inbox/older runs the same fetchOverlayMaps + applyOverlay as /api/inbox.
 * Only the mailbox/folder context and the provenance flags have to be supplied.
 *
 * isSharedMailbox is LOAD-BEARING, not decoration: the Shared Inbox tab derives its list
 * with `.filter(e => e.isSharedMailbox)`, so a row without it is silently dropped and
 * "Load older" appears to do nothing at all.
 *
 * toRecipients/ccRecipients arrive on the row since ENG-98 (the archive select now carries
 * the 087 jsonb columns), so older rows render the same "To:" line as the main window.
 */
export function clientEmailFromInboxMessage(
  m: InboxMessage,
  opts: { mailbox: string; folder?: string },
): ClientEmail {
  return {
    id: m.id,
    subject: m.subject,
    bodyPreview: m.snippet,
    receivedDateTime: m.receivedDateTime,
    hasAttachments: m.hasAttachments,
    isRead: m.isRead,
    from: m.from ? { emailAddress: m.from } : null,
    toRecipients: (m.toRecipients ?? []).map((a) => ({ emailAddress: a })),
    ccRecipients: (m.ccRecipients ?? []).map((a) => ({ emailAddress: a })),
    originally_to: m.originallyTo ?? null,
    internal_recipients: m.internalRecipients ?? [],
    mailboxAddress: opts.mailbox,
    // Only account-mapped mailboxes are ever paged from this tab, so both are true by
    // construction (the /api/inbox/older handler 404s anything else).
    isSharedMailbox: true,
    via: "client-mailbox",
    isPersonalMailbox: false,
    folder: opts.folder ?? "inbox",
    categories: m.categories ?? [],
    flag: m.flagStatus ? { flagStatus: m.flagStatus as "notFlagged" | "flagged" | "complete" } : null,
    taskStatus: m.taskStatus,
    assignedToUserId: m.assignedToUserId,
    assignedToName: m.assignedToName,
    taskNotes: m.taskNotes,
    pinned: m.pinned === true,
    pinnedProjectId: m.pinnedProjectId,
    pinnedProjectTitle: m.pinnedProjectTitle,
  };
}
export function fromInboxMessage(m: InboxMessage, group: InboxGroup): UnifiedEmail {
  return {
    id: m.id,
    mailbox: group.mailbox_address,
    accountId: group.account_id,
    subject: m.subject,
    snippet: m.snippet,
    receivedDateTime: m.receivedDateTime,
    fromName: m.from?.name ?? null,
    fromAddress: m.from?.address ?? null,
    toRecipients: m.toRecipients ?? [],
    ccRecipients: m.ccRecipients ?? [],
    isRead: m.isRead,
    hasAttachments: m.hasAttachments,
    flagStatus: m.flagStatus,
    categories: m.categories ?? [],
    taskStatus: m.taskStatus,
    assignedToUserId: m.assignedToUserId,
    assignedToName: m.assignedToName,
    taskNotes: m.taskNotes,
    pinned: m.pinned === true,
    pinnedProjectId: m.pinnedProjectId,
    pinnedProjectTitle: m.pinnedProjectTitle,
    conversationId: m.conversationId ?? null,
    isOwnPersonalMailbox: m.isOwnPersonalMailbox === true,
    originallyTo: m.originallyTo ?? null,
    internalRecipients: m.internalRecipients ?? [],
  };
}

/** "My Mail" (Phase 2): GET /api/inbox/mine has no account and no claim/pin overlay —
 * accountId is "" (no client to route pin/task-linking to) and isOwnPersonalMailbox gates
 * those actions off in EmailRow/useEmailActions. */
export function fromMyMailMessage(m: InboxMessage, mailbox: string): UnifiedEmail {
  return {
    id: m.id,
    mailbox,
    accountId: "",
    subject: m.subject,
    snippet: m.snippet,
    receivedDateTime: m.receivedDateTime,
    fromName: m.from?.name ?? null,
    fromAddress: m.from?.address ?? null,
    isRead: m.isRead,
    hasAttachments: m.hasAttachments,
    flagStatus: m.flagStatus,
    categories: m.categories ?? [],
    pinned: false,
    conversationId: m.conversationId ?? null,
    isOwnPersonalMailbox: true,
  };
}

/** Live overlay rows the account page already caches (["email-tasks", id] / ["pinned-emails-map", id]).
 * When provided they win over the server-embedded overlay — they receive optimistic updates. */
export interface LiveOverlays {
  task?: {
    status?: string;
    assigned_to_user_id?: string | null;
    notes?: string | null;
    users?: { name?: string | null } | null;
  } | null;
  pin?: { project_id?: string | null; project_title?: string | null } | null;
}

export function fromClientEmail(e: ClientEmail, accountId: string, live?: LiveOverlays): UnifiedEmail {
  const task = live?.task;
  const pin = live?.pin;
  return {
    id: e.id,
    mailbox: e.mailboxAddress,
    accountId,
    subject: e.subject,
    snippet: e.bodyPreview,
    receivedDateTime: e.receivedDateTime,
    fromName: e.from?.emailAddress?.name ?? null,
    fromAddress: e.from?.emailAddress?.address ?? null,
    toRecipients: (e.toRecipients ?? []).map((r) => r.emailAddress),
    ccRecipients: (e.ccRecipients ?? []).map((r) => r.emailAddress),
    isRead: e.isRead,
    hasAttachments: e.hasAttachments,
    flagStatus: e.flag?.flagStatus,
    categories: e.categories ?? [],
    taskStatus: task ? task.status : e.taskStatus,
    assignedToUserId: task ? (task.assigned_to_user_id ?? undefined) : e.assignedToUserId,
    assignedToName: task ? (task.users?.name ?? undefined) : e.assignedToName,
    taskNotes: task ? (task.notes ?? undefined) : e.taskNotes,
    pinned: pin ? true : e.pinned === true,
    pinnedProjectId: pin ? (pin.project_id ?? undefined) : e.pinnedProjectId,
    pinnedProjectTitle: pin ? (pin.project_title ?? undefined) : e.pinnedProjectTitle,
    via: e.via,
    isPersonalMailbox: e.isPersonalMailbox,
    folder: e.folder,
    originallyTo: e.originally_to ?? null,
    internalRecipients: e.internal_recipients ?? [],
  };
}

/** Build the ClientEmail shape EmailReplyModal expects from a unified row. */
export function toReplyEmail(u: UnifiedEmail): ClientEmail {
  return {
    id: u.id,
    subject: u.subject,
    bodyPreview: u.snippet,
    receivedDateTime: u.receivedDateTime,
    hasAttachments: u.hasAttachments,
    isRead: u.isRead,
    from: u.fromAddress ? { emailAddress: { name: u.fromName ?? "", address: u.fromAddress } } : null,
    toRecipients: (u.toRecipients ?? []).map((a) => ({ emailAddress: a })),
    ccRecipients: (u.ccRecipients ?? []).map((a) => ({ emailAddress: a })),
    mailboxAddress: u.mailbox,
  };
}
